// src/pages/ProjectDashboard/ProjectDashboard.jsx
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import axiosInstace from "../../axiosInstace";
import { useAuth } from "../../contexts/AuthContext";
import "./ProjectDashboard.scss";
import TableView from "../../components/TableView/TableView";
import ButtonWithIcon from "../../components/ButtonwithIcon/ButtonWithIcon";
import CreateTaskModal from "../../components/CreateTaskModal/CreateTaskModal";
import { createPortal } from "react-dom";
import useProjectPermissions from "../../hooks/useProjectPermissions";
import Toast from "../../components/Toast/Toast";
import KanbanView from "../../components/KanbanView/KanbanView";
import GanttView from "../../components/GanttView/GanttView";
import { normalizeRole, PERM, ROLES } from "../../config/roles";
import UpdateProjectMembers from "../../components/UpdateProjectMembers/UpdateProjectMembers";
import DetailsModal from "../../components/DetailsModal/DetailsModal";
import InputField from "../../components/InputComponent/InputField";
import StagesIcon from "../../assets/StagesIcon.png";
import EditStagesModal from "../../components/EditStagesModal/EditStagesModal";
import ListView from "../../components/ListView/ListView";
import DeleteButton from "../../components/DeleteButton/DeleteButton";
import DeleteWarningModal from "../../components/DeleteWarningModal/DeleteWarningModal";
import ViewToggle from "../../components/ViewToggle/ViewToggle";
import QuickAddModal from "../../components/QuickAddModal/QuickAddModal";
import FilterBar from "../../components/FilterBar/FilterBar";
import ProjectSkeleton from "../../components/ProjectSkeleton/ProjectSkeleton";
import ForceRemoveMemberModal from "../../components/ForceRemoveMemberModal/ForceRemoveMemberModal";
import DuplicateProjectModal from "./Duplicateprojectmodal";
import { FiCopy, FiUsers, FiMoreVertical, FiInfo } from "react-icons/fi";
import CustomTooltip from "../../components/CustomTooltip/CustomTooltip";
import EmptyState from "../../components/EmptyState/EmptyState";
import StatusBadge from "../../components/StatusBadge/StatusBadge";
import ProgressBar from "../../components/ProgressBar/ProgressBar";
import { resolveAssigneeId } from "../../components/AssignMemberSelect/AssignMemberSelect";
import EmployeeTable from "../../components/EmployeeTable/EmployeeTable";

// Member endpoints. `allmembers`, `members/update`, DELETE `members/:empId`
// and `members/add` (per the Swagger docs) are confirmed.
const MEMBER_API = {
  add: (projectId) =>
    `/api/emp/projects/projectmemberaccess/${projectId}/members/add`,
};

export default function ProjectDashboard() {
  const { projectId } = useParams();
  const { user, authUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [view, setView] = useState(searchParams.get("view") || "table");
  const [project, setProject] = useState(null);
  const [loadingProject, setLoadingProject] = useState(true);
  const [members, setMembers] = useState([]);
  const [stages, setStages] = useState([]);
  const [openTaskModal, setOpenTaskModal] = useState(null);
  const [showStageModal, setShowStageModal] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState(null);

  const prevPermLoadingRef = useRef(false);
  const [showInfoModal, setShowInfoModal] = useState(false);

  const [toastOpen, setToastOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  const [toastType, setToastType] = useState("success");

  const [openTodoModal, setOpenTodoModal] = useState(null);
  const [roleUpdatingFor, setRoleUpdatingFor] = useState(null);
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showQuickAddModal, setShowQuickAddModal] = useState(false);

  // 3-dot "more actions" menu in the compact header
  const [showMoreActions, setShowMoreActions] = useState(false);
  const moreMenuRef = useRef(null);

  const titleRef = useRef(null);
  const [forceRemoveTarget, setForceRemoveTarget] = useState(null);
  const [forceDeleteState, setForceDeleteState] = useState({
    open: false,
    message: "",
  });

  const [filters, setFilters] = React.useState({
    q: "",
    status: "all",
    assignee: "all",
    sort: "newest",
  });

  const [selectedRoleFor, setSelectedRoleFor] = useState({});

  // Add Member panel (Details modal → Members)
  const [showAddMember, setShowAddMember] = useState(false);
  const [addingMember, setAddingMember] = useState(false);
  // Employees (with roles) picked in <EmployeeTable/>, not yet saved.
  const [pendingMembers, setPendingMembers] = useState([]);
  // Same pattern as CreateProject: memoised + equality guard so the picker's
  // onAdd callback can't cause a render loop.
  const handlePickedMembers = useCallback((membersArray) => {
    const next = (membersArray || []).map((m) => ({
      empId: Number(m.empId),
      roleId: Number(m.roleId),
      empName: m.empName,
    }));
    setPendingMembers((prev) =>
      prev.length === next.length &&
      prev.every(
        (m, i) => m.empId === next[i].empId && m.roleId === next[i].roleId,
      )
        ? prev
        : next,
    );
  }, []);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showUpdateMembersModal, setShowUpdateMembersModal] = useState(false);

  const storedUser = (() => {
    try {
      return JSON.parse(sessionStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  })();

  const currentEmpId = user?.empId ?? storedUser?.empId ?? null;

  const availableRoles = [
    { roleId: 1, roleType: ROLES.CREATOR },
    { roleId: 2, roleType: ROLES.APPROVER },
    { roleId: 3, roleType: ROLES.DOER },
    { roleId: 4, roleType: ROLES.VIEWER },
    { roleId: 5, roleType: ROLES.CLIENT },
  ];

  const skipAutoRefreshRef = useRef(false);
  const closeToast = useCallback(() => setToastOpen(false), []);
  const [stageChangeLoading, setStageChangeLoading] = useState({});
  const creatorEmpId = project?.createdByEmpId ?? null;

  const {
    loading: permLoading,
    error: permError,
    members: projectMembersHook,
    member: currentUserMemberHook,
    can: canPerm,
    refresh: refreshProjectMembers,
  } = useProjectPermissions(projectId, {
    userEmpId: currentEmpId,
    axiosInstance: axiosInstace,
  });

  const rawRole =
    currentUserMemberHook?.roleType ??
    projectMembersHook?.find((m) => String(m.empId) === String(authUser?.empId))
      ?.roleType ??
    project?.role ??
    user?.roleType ??
    user?.roleName ??
    user?.roleId;

  const resolvedUserRole = normalizeRole(rawRole);
  const isReadOnlyRole =
    resolvedUserRole === ROLES.CLIENT || resolvedUserRole === ROLES.VIEWER;
  // Only Creator and Approver may add members (and, in the create modals,
  // assign a stage/task to someone else).
  const canAddMembers =
    resolvedUserRole === ROLES.CREATOR || resolvedUserRole === ROLES.APPROVER;
  const canDelete = !permLoading && canPerm("TASK_DELETE");
  const isApproverRole =
    (currentUserMemberHook?.roleType || "").toUpperCase() === "APPROVER";

  const canChangeTodoStageFor = useCallback(
    (todo, parentTask) => {
      if (!todo) return false;
      if (isApproverRole) return true;
      if (parentTask && String(parentTask.taskOwnerId) === String(currentEmpId))
        return true;
      if (String(todo.assignedEmpId) === String(currentEmpId)) return true;
      return false;
    },
    [isApproverRole, currentEmpId],
  );

  const normalizeTasks = (projectData) =>
    (projectData?.tasks || []).map((t) => {
      const task = {
        ...t,
        startDate: t.startDate ? String(t.startDate) : null,
        endDate: t.endDate ? String(t.endDate) : null,
      };
      task.todoRequestDTOList = (t.todoRequestDTOList || []).map((td) => {
        const todo = { ...td };
        todo.toDoListResponseDTOList = (td.toDoListResponseDTOList || []).map(
          (li) => {
            const serverVal =
              li?.completion ??
              li?.completionFlag ??
              (li?.status && String(li.status).toUpperCase() === "DONE");
            return {
              ...li,
              completion: Boolean(serverVal),
              status: serverVal ? "DONE" : "NOT_DONE",
            };
          },
        );
        return todo;
      });
      return task;
    });

  const handleDeleteProject = async () => {
    try {
      await axiosInstace.delete(`/api/emp/project/${projectId}`);
      setToastMsg("Project deleted");
      setToastType("success");
      setToastOpen(true);
      window.dispatchEvent(
        new CustomEvent("project-updated", {
          detail: { action: "deleted", projectId },
        }),
      );
      setTimeout(() => navigate("/dashboard"), 300);
    } catch (err) {
      // Show the server's response message first.
      const serverMessage =
        err?.response?.data?.message ||
        err.message ||
        "Failed to delete project";
      setToastMsg(serverMessage);
      setToastType("error");
      setToastOpen(true);
      // Then offer the force-delete option.
      setForceDeleteState({ open: true, message: serverMessage });
    }
  };

  const handleForceDeleteProject = async () => {
    try {
      const res = await axiosInstace.delete(
        `/api/emp/project/force/${projectId}`,
      );
      setToastMsg(res?.data?.message || "Project force deleted");
      setToastType("success");
      setToastOpen(true);
      window.dispatchEvent(
        new CustomEvent("project-updated", {
          detail: { action: "deleted", projectId },
        }),
      );
      setForceDeleteState({ open: false, message: "" });
      setTimeout(() => navigate("/dashboard"), 300);
    } catch (err) {
      setToastMsg(
        err?.response?.data?.message ||
          err.message ||
          "Failed to force delete project",
      );
      setToastType("error");
      setToastOpen(true);
    }
  };

  const handleDeleteTodoList = async (todolistId) => {
    if (!todolistId) return null;
    if (!canPerm("TODOLIST_DELETE")) {
      setToastMsg("You don't have permission to delete checklist items");
      setToastType("error");
      setToastOpen(true);
      return null;
    }
    const previousTasks = tasks;
    try {
      setTasks((prev) =>
        (prev || []).map((task) => ({
          ...task,
          todoRequestDTOList: (task.todoRequestDTOList || []).map((td) => ({
            ...td,
            toDoListResponseDTOList: (td.toDoListResponseDTOList || []).filter(
              (li) => Number(li.toDoListId ?? li.id) !== Number(todolistId),
            ),
          })),
        })),
      );
      const res = await axiosInstace.delete(`/api/emp/todolists/${todolistId}`);
      if (!(res.status >= 200 && res.status < 300))
        throw new Error(res?.data?.message || "Delete failed");
      setToastMsg("Checklist item deleted");
      setToastType("success");
      setToastOpen(true);
      scheduleProjectRefreshDebounced(1200);
      return true;
    } catch (err) {
      try {
        const r = await axiosInstace.get(`/api/emp/project/${projectId}/full`);
        const projectData = r?.data?.data ?? null;
        setProject(projectData);
        setTasks(normalizeTasks(projectData) ?? previousTasks);
      } catch {
        setTasks(previousTasks);
      }
      setToastMsg(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to delete checklist item",
      );
      setToastType("error");
      setToastOpen(true);
      return null;
    }
  };

  useEffect(() => {
    const navbar = document.querySelector(".navbar");
    if (!navbar) return;
    if (showInfoModal) navbar.classList.add("modal-open");
    else navbar.classList.remove("modal-open");
    return () => navbar.classList.remove("modal-open");
  }, [showInfoModal]);

  const handleCreateTodo = async (payload) => {
    try {
      // Creator/Approver may pick any project member; for everyone else, or
      // when nobody is picked, the logged-in user owns the task.
      const assigneeId = resolveAssigneeId({
        selectedEmpId: payload.assignedEmpId,
        currentEmpId,
        members,
        currentUserRole: resolvedUserRole,
        scope: "todo",
      });
      const assigneeIsSomeoneElse =
        assigneeId != null && String(assigneeId) !== String(currentEmpId);
      // accessId identifies the assignee's project access record, so it must
      // follow the resolved assignee (never trust one passed in by the caller).
      const assigneeAccessId = assigneeIsSomeoneElse
        ? (members.find((m) => String(m.empId) === String(assigneeId))
            ?.accessId ?? null)
        : (getCurrentAccessId() ?? project?.accessId ?? null);

      const res = await axiosInstace.post(
        "/api/emp/todos",
        {
          toDoTitle: payload.toDoTitle,
          remark: payload.remark ?? null,
          priority: payload.priority ?? "MEDIUM",
          taskId: Number(payload.taskId),
          stagePositionId: payload.stagePositionId ?? 1,
          assignedEmpId: assigneeId,
          accessId: assigneeAccessId,
          listRequestDTOS: payload.listRequestDTOS ?? [],
        },
        { headers: { "Content-Type": "application/json" } },
      );
      setToastMsg("Task created");
      setToastType("success");
      setToastOpen(true);
      scheduleProjectRefreshDebounced(1200);
      return res?.data?.data ?? null;
    } catch (err) {
      const status = err?.response?.status;
      const message = err?.response?.data?.message;
      if (
        status === 409 &&
        typeof message === "string" &&
        message.toLowerCase().includes("already exists")
      ) {
        setToastMsg(message);
        setToastType("warning");
        setToastOpen(true);
        return { status: 409, message };
      }
      setToastMsg(message || err?.message || "Failed to create task");
      setToastType("error");
      setToastOpen(true);
      return null;
    }
  };

  const handleCreateTodoList = async (payload) => {
    try {
      const res = await axiosInstace.post("/api/emp/todolists/create", payload);
      setToastMsg("Checklist item added");
      setToastType("success");
      setToastOpen(true);
      scheduleProjectRefreshDebounced(1000);
      return res?.data?.data ?? null;
    } catch (err) {
      const status = err?.response?.status;
      const message = err?.response?.data?.message;
      if (
        status === 409 &&
        typeof message === "string" &&
        message.toLowerCase().includes("already exists")
      ) {
        setToastMsg(message);
        setToastType("warning");
        setToastOpen(true);
        return null;
      }
      setToastMsg(message || err?.message || "Failed to add checklist item");
      setToastType("error");
      setToastOpen(true);
      return null;
    }
  };

  const handleUpdateTodo = async (todoId, patch = {}) => {
    if (!todoId) return null;
    if (!canPerm("TODO_UPDATE")) {
      setToastMsg("You don't have permission to update tasks");
      setToastType("error");
      setToastOpen(true);
      return null;
    }
    const allowedKeys = ["toDoTitle", "remark", "accessId"];
    const payload = {};
    Object.keys(patch || {}).forEach((k) => {
      if (allowedKeys.includes(k)) payload[k] = patch[k];
    });
    if (!payload.accessId && project?.accessId)
      payload.accessId = project.accessId;

    const previousTasks = tasks;
    try {
      setTasks((prev) =>
        (prev || []).map((t) => {
          if (!Array.isArray(t.todoRequestDTOList)) return t;
          return {
            ...t,
            todoRequestDTOList: t.todoRequestDTOList.map((td) =>
              Number(td.toDoId ?? td.id) === Number(todoId)
                ? { ...td, ...payload }
                : td,
            ),
          };
        }),
      );
      setProject((prev) => ({
        ...prev,
        tasks: (prev?.tasks || []).map((t) => {
          if (!Array.isArray(t.todoRequestDTOList)) return t;
          return {
            ...t,
            todoRequestDTOList: t.todoRequestDTOList.map((td) =>
              Number(td.toDoId ?? td.id) === Number(todoId)
                ? { ...td, ...payload }
                : td,
            ),
          };
        }),
      }));
      const res = await axiosInstace.put(`/api/emp/todos/${todoId}`, payload, {
        headers: { "Content-Type": "application/json" },
      });
      if (!(res.status >= 200 && res.status < 300))
        throw new Error(
          res?.data?.message || `Unexpected status ${res.status}`,
        );
      if (typeof scheduleProjectRefreshDebounced === "function")
        scheduleProjectRefreshDebounced(1200);
      setToastMsg("Task updated");
      setToastType("info");
      setToastOpen(true);
      return res?.data?.data ?? null;
    } catch (err) {
      const serverMessage =
        err?.response?.data?.message || err?.message || "Failed to update task";
      setToastMsg(serverMessage);
      setToastType("error");
      setToastOpen(true);
      try {
        const r = await axiosInstace.get(`/api/emp/project/${projectId}/full`);
        const projectData = r?.data?.data ?? null;
        setProject(projectData);
        setTasks(normalizeTasks(projectData) ?? previousTasks);
      } catch {
        setTasks(previousTasks);
      }
      return null;
    }
  };

  const [remarkModal, setRemarkModal] = useState({
    open: false,
    todoId: null,
    title: "",
    remark: "",
  });
  const handleOpenTodoRemarkModal = (todoId, title, remark) =>
    setRemarkModal({ open: true, todoId, title, remark });
  const handleCloseTodoRemarkModal = () =>
    setRemarkModal((s) => ({ ...s, open: false }));

  const fmt = (d) => {
    if (!d) return "—";
    const date = new Date(d);
    if (isNaN(date)) return String(d);
    return date.toLocaleDateString();
  };

  const getCurrentAccessId = useCallback(() => {
    const uId =
      user?.empId ??
      JSON.parse(sessionStorage.getItem("user") || "null")?.empId;
    const m = (members || []).find((m) => String(m.empId) === String(uId));
    return m?.accessId ?? null;
  }, [members, user]);

  const handleUpdateTask = async (taskId, patch = {}) => {
    if (!taskId) return null;
    if (!patch.accessId && project?.accessId) patch.accessId = project.accessId;
    if (!canPerm("TASK_UPDATE")) {
      setToastMsg("You don't have permission to update stage");
      setToastType("error");
      setToastOpen(true);
      return null;
    }

    const previousTasks = tasks;
    const allowedKeys = [
      "taskTitle",
      "description",
      "schedulingMode",
      "startDate",
      "endDate",
      "durationDays",
      "priority",
      "remark",
      "accessId",
    ];
    const payload = {};
    Object.keys(patch).forEach((k) => {
      if (allowedKeys.includes(k)) payload[k] = patch[k];
    });

    const toISO = (d) => {
      if (!d) return undefined;
      const date = new Date(d);
      if (isNaN(date)) return String(d);
      return date.toISOString().split("T")[0];
    };
    if (payload.startDate) payload.startDate = toISO(payload.startDate);
    if (payload.endDate) payload.endDate = toISO(payload.endDate);

    const storedUserFallback = (() => {
      try {
        return JSON.parse(sessionStorage.getItem("user") || "null");
      } catch {
        return null;
      }
    })();
    const empId = user?.empId ?? storedUser?.empId ?? null;
    const empName = user?.empName ?? storedUserFallback?.empName ?? null;

    if (!payload.accessId && project?.accessId)
      payload.accessId = project.accessId;
    const existingTask = (tasks || []).find(
      (t) => Number(t.taskId ?? t.id) === Number(taskId),
    );
    // Keep the current owner on edits. Only fall back to the logged-in user
    // when the stage has no owner yet; otherwise an Approver's assignment
    // would be overwritten by whoever edits the stage next.
    if (!payload.taskOwnerId) {
      const ownerId = existingTask?.taskOwnerId ?? empId;
      if (ownerId != null) payload.taskOwnerId = ownerId;
    }
    if (!payload.taskOwnerName) {
      const ownerName =
        existingTask?.taskOwnerName ??
        (members || []).find(
          (m) => String(m.empId) === String(payload.taskOwnerId),
        )?.empName ??
        empName;
      if (ownerName) payload.taskOwnerName = ownerName;
    }

    const durNum = Number(payload.durationDays);
    const hasDur = Number.isFinite(durNum) && durNum > 0;
    const existingStartISO = existingTask?.startDate
      ? toISO(existingTask.startDate)
      : new Date().toISOString().split("T")[0];

    if (payload.startDate && payload.endDate) {
      payload.schedulingMode = "DATE";
      delete payload.durationDays;
    } else {
      payload.schedulingMode = "DAY";
      payload.startDate = payload.startDate || existingStartISO;
      payload.durationDays = hasDur ? Math.max(1, durNum) : 1;
      delete payload.endDate;
    }
    if (payload.priority)
      payload.priority = String(payload.priority).toUpperCase();
    if (payload.schedulingMode)
      payload.schedulingMode = String(payload.schedulingMode).toUpperCase();

    try {
      setTasks((prev) =>
        (prev || []).map((t) =>
          Number(t.taskId ?? t.id) === Number(taskId) ? { ...t, ...patch } : t,
        ),
      );
      setProject((prev) => ({
        ...prev,
        tasks: (prev?.tasks || []).map((t) =>
          Number(t.taskId ?? t.id) === Number(taskId) ? { ...t, ...patch } : t,
        ),
      }));

      const res = await axiosInstace.put(`/api/emp/tasks/${taskId}`, payload, {
        headers: { "Content-Type": "application/json" },
      });
      if (!(res.status >= 200 && res.status < 300))
        throw new Error(
          res?.data?.message || `Unexpected status ${res.status}`,
        );

      setToastMsg("Stage updated");
      setToastType("info");
      setToastOpen(true);
      return res?.data?.data ?? null;
    } catch (err) {
      const serverMessage =
        err?.response?.data?.message ||
        (err?.response?.data && JSON.stringify(err.response.data)) ||
        err?.message ||
        "Failed to update task";
      if (
        String(serverMessage).toLowerCase().includes("final") ||
        String(serverMessage).toLowerCase().includes("incomplete")
      ) {
        setToastMsg(
          "Cannot move to final stage — all child Tasks and checklist items must be completed first.",
        );
      } else {
        setToastMsg(serverMessage);
      }
      setToastType("error");
      setToastOpen(true);
      try {
        const r = await axiosInstace.get(`/api/emp/project/${projectId}/full`);
        setProject(r?.data?.data ?? null);
        setTasks(r?.data?.data?.tasks ?? previousTasks);
      } catch {
        setTasks(previousTasks);
      }
      return null;
    }
  };

  const refreshProjectFull = async (projectIdArg = project?.projectId) => {
    if (!projectIdArg) return;
    try {
      const res = await axiosInstace.get(
        `/api/emp/project/${projectIdArg}/full`,
      );
      const payload = res?.data?.data ?? res?.data ?? null;
      if (!payload) return;

      const tasksFromResp =
        payload.taskResponseDTOList ||
        payload.tasks ||
        payload.taskList ||
        payload.taskResponseList ||
        payload.taskResponseDTOs ||
        null;
      const stagesFromResp =
        payload.stagePositionResponseDTOList ||
        payload.stages ||
        payload.stagePositionResponseDTOs ||
        payload.stageList ||
        null;

      if (payload.project || payload.projectResponse || payload.projectDTO) {
        setProject(
          payload.project || payload.projectResponse || payload.projectDTO,
        );
      }
      if (Array.isArray(tasksFromResp)) setTasks(tasksFromResp);
      if (Array.isArray(stagesFromResp)) setStages(stagesFromResp);
      if (
        Array.isArray(payload.projectMemberAccessDTOList) ||
        Array.isArray(payload.members)
      ) {
        setMembers(payload.projectMemberAccessDTOList || payload.members);
      }
    } catch (err) {
      console.error("[refreshProjectFull] failed:", err?.response?.data ?? err);
    }
  };

  const handleUpdateTodoList = async (todolistId, patch = {}) => {
    if (!todolistId) return null;
    if (!canPerm("TODOLIST_UPDATE")) {
      setToastMsg("You don't have permission to update checklist items");
      setToastType("error");
      setToastOpen(true);
      return null;
    }
    const previousTasks = tasks;
    try {
      setTasks((prev) =>
        (prev || []).map((task) => {
          if (!Array.isArray(task.todoRequestDTOList)) return task;
          return {
            ...task,
            todoRequestDTOList: task.todoRequestDTOList.map((td) => {
              if (!Array.isArray(td.toDoListResponseDTOList)) return td;
              return {
                ...td,
                toDoListResponseDTOList: td.toDoListResponseDTOList.map((li) =>
                  Number(li.toDoListId ?? li.id) === Number(todolistId)
                    ? { ...li, ...patch }
                    : li,
                ),
              };
            }),
          };
        }),
      );
      const res = await axiosInstace.put(
        `/api/emp/todolists/${todolistId}/title`,
        patch,
        { headers: { "Content-Type": "application/json" } },
      );
      if (!(res.status >= 200 && res.status < 300))
        throw new Error(
          res?.data?.message || `Unexpected status ${res.status}`,
        );
      setToastMsg("Checklist item updated");
      setToastType("info");
      setToastOpen(true);
      return res?.data?.data ?? null;
    } catch (err) {
      const serverMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to update checklist";
      setToastMsg(serverMessage);
      setToastType("error");
      setToastOpen(true);
      try {
        const r = await axiosInstace.get(`/api/emp/project/${projectId}/full`);
        setProject(r?.data?.data ?? null);
        setTasks(r?.data?.data?.tasks ?? previousTasks);
      } catch {
        setTasks(previousTasks);
      }
      return null;
    }
  };

  // Update a checklist item's remark and/or mark it approved (completion).
  // Reuses the existing title/patch endpoint for the remark text and the
  // existing completion endpoint for approval, so both stay in sync with
  // the same optimistic-update + rollback pattern used elsewhere.
  const handleUpdateChecklistRemark = async (
    toDoListId,
    { remark, completionFlag } = {},
  ) => {
    if (toDoListId === undefined || toDoListId === null) return null;
    if (!canPerm("TODOLIST_UPDATE")) {
      setToastMsg("You don't have permission to update checklist items");
      setToastType("error");
      setToastOpen(true);
      return null;
    }

    let result = null;
    if (remark !== undefined) {
      result = await handleUpdateTodoList(toDoListId, { remark });
    }
    if (completionFlag === true) {
      result = await handleToggleTodoListCompletion(toDoListId, true);
    }
    return result;
  };

  const handleChangeTaskStage = async (
    taskId,
    stagePositionId,
    stageName = "",
    isFinal = false,
  ) => {
    if (!taskId || !stagePositionId) return null;
    if (!canPerm("TASK_UPDATE")) {
      setToastMsg("You don't have permission to change stage's status");
      setToastType("error");
      setToastOpen(true);
      return null;
    }
    const prevProject = project;
    const prevTasks = tasks;
    setStageChangeLoading((s) => ({ ...s, [taskId]: true }));
    setTasks((prev) =>
      (prev || []).map((t) =>
        Number(t.taskId ?? t.id) === Number(taskId)
          ? { ...t, stagePositionId }
          : t,
      ),
    );
    try {
      const res = await axiosInstace.put(
        `/api/emp/tasks/automate/${taskId}`,
        { stagePositionId },
        { headers: { "Content-Type": "application/json" } },
      );
      scheduleProjectRefreshDebounced(1500);
      setToastMsg(`Stage moved to ${stageName || "selected status"}.`);
      setToastType("success");
      setToastOpen(true);
      return res?.data?.data ?? null;
    } catch (err) {
      skipAutoRefreshRef.current = true;
      setProject(prevProject);
      setTasks(prevTasks);
      const serverMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to change stage";
      if (
        err?.response?.status === 400 ||
        String(serverMessage).toLowerCase().includes("incomplete") ||
        String(serverMessage).toLowerCase().includes("final")
      ) {
        setToastMsg(
          "Cannot move to final stage — all child Tasks and checklist items must be completed first",
        );
      } else {
        setToastMsg(serverMessage);
      }
      setToastType("error");
      setToastOpen(true);
      return null;
    } finally {
      setStageChangeLoading((s) => ({ ...s, [taskId]: false }));
    }
  };

  const handleChangeTodoStage = async (todoId, stagePositionId) => {
    if (!todoId || !stagePositionId) return null;
    if (!canPerm("TODO_UPDATE")) {
      setToastMsg("You don't have permission to change task stage");
      setToastType("error");
      setToastOpen(true);
      return null;
    }
    const prevProject = project;
    const prevTasks = tasks;
    setTasks((prev) =>
      (prev || []).map((t) => ({
        ...t,
        todoRequestDTOList: (t.todoRequestDTOList || []).map((td) =>
          Number(td.toDoId ?? td.id) === Number(todoId)
            ? { ...td, stagePositionId }
            : td,
        ),
      })),
    );
    try {
      const res = await axiosInstace.put(
        `/api/emp/todo/automate/${todoId}`,
        { stagePositionId },
        { headers: { "Content-Type": "application/json" } },
      );
      scheduleProjectRefreshDebounced(1500);
      setToastMsg("Task moved to new stage");
      setToastType("success");
      setToastOpen(true);
      return res?.data?.data ?? null;
    } catch (err) {
      skipAutoRefreshRef.current = true;
      setProject(prevProject);
      setTasks(prevTasks);
      setToastMsg(
        err?.response?.data?.message || err?.message || "Failed to move task",
      );
      setToastType("error");
      setToastOpen(true);
      return null;
    }
  };

  const refreshTimerRef = useRef(null);
  const scheduleProjectRefreshDebounced = useCallback(
    (ms = 1500) => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = setTimeout(async () => {
        try {
          const res = await axiosInstace.get(
            `/api/emp/project/${projectId}/full`,
          );
          const projectData = res?.data?.data ?? null;
          setProject(projectData);
          setTasks(normalizeTasks(projectData));
        } catch {
          /* ignore */
        }
      }, ms);
    },
    [projectId],
  );

  useEffect(() => {
    if (!showInfoModal) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setShowInfoModal(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showInfoModal]);

  // Close the 3-dot header menu on outside click / Escape
  useEffect(() => {
    if (!showMoreActions) return;
    const handleClickOutside = (e) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target)) {
        setShowMoreActions(false);
      }
    };
    const handleEscape = (e) => {
      if (e.key === "Escape") setShowMoreActions(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("keydown", handleEscape);
    };
  }, [showMoreActions]);

  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (Array.isArray(projectMembersHook)) setMembers(projectMembersHook);
  }, [projectMembersHook]);

  const handleToggleTodoListCompletion = async (toDoListId, completionFlag) => {
    if (toDoListId === undefined || toDoListId === null) return null;
    if (!canPerm("TODOLIST_UPDATE")) {
      setToastMsg("You don't have permission to update checklist items");
      setToastType("error");
      setToastOpen(true);
      return null;
    }
    const listKey = Number(toDoListId);
    const previousTasks = tasks;
    setTasks((prev) =>
      (prev || []).map((t) => ({
        ...t,
        todoRequestDTOList: (t.todoRequestDTOList || []).map((td) => ({
          ...td,
          toDoListResponseDTOList: (td.toDoListResponseDTOList || []).map(
            (li) => {
              const liKey = Number(li.toDoListId ?? li.id);
              return liKey === listKey
                ? {
                    ...li,
                    completion: !!completionFlag,
                    status: completionFlag ? "DONE" : "NOT_DONE",
                  }
                : li;
            },
          ),
        })),
      })),
    );
    const url = `/api/emp/todolists/${listKey}/completion?completionFlag=${!!completionFlag}`;
    try {
      const res = await axiosInstace.put(url);
      scheduleProjectRefreshDebounced(1500);
      const payload = res?.data?.data;
      if (payload && typeof payload === "object") {
        setTasks((prev) =>
          (prev || []).map((t) => ({
            ...t,
            todoRequestDTOList: (t.todoRequestDTOList || []).map((td) => ({
              ...td,
              toDoListResponseDTOList: (td.toDoListResponseDTOList || []).map(
                (li) => {
                  const liKey = Number(li.toDoListId ?? li.id);
                  if (liKey !== listKey) return li;
                  const serverVal =
                    payload?.completion ??
                    payload?.completionFlag ??
                    (payload?.status &&
                      String(payload.status).toUpperCase() === "DONE");
                  return {
                    ...li,
                    completion:
                      typeof serverVal === "boolean"
                        ? serverVal
                        : li.completion,
                    status:
                      payload?.status ??
                      (serverVal === true ? "DONE" : "NOT_DONE"),
                  };
                },
              ),
            })),
          })),
        );
      }
      setToastMsg("Checklist updated");
      setToastType("success");
      setToastOpen(true);
      return payload ?? null;
    } catch (err) {
      try {
        const res = await axiosInstace.get(
          `/api/emp/project/${projectId}/full`,
        );
        const projectData = res?.data?.data ?? null;
        setProject(projectData);
        setTasks(normalizeTasks(projectData) ?? previousTasks);
      } catch {
        setTasks(previousTasks);
      }
      setToastMsg(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to update checklist",
      );
      setToastType("error");
      setToastOpen(true);
      return null;
    }
  };

  const handleSwitchView = (newView) => {
    setView(newView);
    setSearchParams({ view: newView });
  };

  function getStagePosition(stage) {
    const list =
      stage.stagePositionResponseDTOList ||
      stage.stagePositionDTOList ||
      stage.stagePositionRequestDTOList ||
      stage.stagePositionDTO ||
      stage.positions ||
      [];
    if (!Array.isArray(list) || list.length === 0)
      return Number.POSITIVE_INFINITY;
    const nums = list
      .map((p) =>
        Number(
          p.position ?? p.pos ?? p.positionIndex ?? p.positionNumber ?? NaN,
        ),
      )
      .filter((n) => Number.isFinite(n));
    return nums.length ? Math.min(...nums) : Number.POSITIVE_INFINITY;
  }

  const normalizeAndSortStages = useCallback((rawStages = []) => {
    return (Array.isArray(rawStages) ? rawStages : []).slice().sort((a, b) => {
      const pa = getStagePosition(a);
      const pb = getStagePosition(b);
      if (pa === pb)
        return String(a.stageName || a.stageTitle || "").localeCompare(
          String(b.stageName || b.stageTitle || ""),
        );
      return pa - pb;
    });
  }, []);

  useEffect(() => {
    if (!projectId) return;
    refreshProjectMembers?.();
  }, [projectId, refreshProjectMembers]);

  useEffect(() => {
    if (skipAutoRefreshRef.current) return;

    const normalizeAndDedupe = (rawStages = []) => {
      const seenStagePos = new Set();
      const seenStageKeys = new Set();
      const outStages = [];
      for (const s of rawStages) {
        const keyParts = [
          (s.stageName ?? s.stageTitle ?? s.title ?? "").trim(),
          String(s.stagePositionResponseDTOList?.[0]?.stagePositionId ?? ""),
        ];
        const stageKey = keyParts.join("|");
        if (seenStageKeys.has(stageKey)) continue;
        seenStageKeys.add(stageKey);
        const cleanPositions = (s.stagePositionResponseDTOList || []).filter(
          (pos) => {
            const id = String(pos.stagePositionId ?? pos.id ?? "");
            if (!id) return false;
            if (seenStagePos.has(id)) return false;
            seenStagePos.add(id);
            return true;
          },
        );
        if (cleanPositions.length === 0) continue;
        outStages.push({ ...s, stagePositionResponseDTOList: cleanPositions });
      }
      return outStages;
    };

    async function loadStages() {
      try {
        const res = await axiosInstace.get(
          `/api/emp/project/${projectId}/stages`,
        );
        const raw = res?.data?.data ?? [];
        let stagesPayload = [];
        if (Array.isArray(raw) && raw.length > 0) {
          if (raw[0] && Array.isArray(raw[0].stageResponseDTOList)) {
            stagesPayload = raw.flatMap(
              (cat) => cat.stageResponseDTOList || [],
            );
          } else if (
            raw[0] &&
            (Array.isArray(raw[0].stagePositionResponseDTOList) ||
              raw[0].stagePositionResponseDTOList !== undefined)
          ) {
            stagesPayload = raw;
          } else {
            stagesPayload = raw.flatMap((r) => {
              if (Array.isArray(r.stageResponseDTOList))
                return r.stageResponseDTOList;
              if (Array.isArray(r.stagePositionResponseDTOList)) return [r];
              if (Array.isArray(r.stageList)) return r.stageList;
              return [];
            });
          }
        }
        const deduped = normalizeAndDedupe(stagesPayload || []);
        const sorted = normalizeAndSortStages(deduped);
        setStages(sorted);
      } catch (err) {
        console.error("Failed to fetch stages:", err);
        setStages([]);
      }
    }

    if (projectId) loadStages();
  }, [projectId, normalizeAndSortStages]);

  const loadProject = useCallback(async () => {
    if (skipAutoRefreshRef.current) {
      setLoadingProject(false);
      return;
    }
    setLoadingProject(true);
    try {
      const res = await axiosInstace.get(`/api/emp/project/${projectId}/full`);
      const projectData = res?.data?.data ?? null;
      const normalizedTasks = (projectData?.tasks || []).map((t) => {
        const task = {
          ...t,
          startDate: t.startDate ? String(t.startDate) : null,
          endDate: t.endDate ? String(t.endDate) : null,
        };
        task.todoRequestDTOList = (t.todoRequestDTOList || []).map((td) => {
          const todo = { ...td };
          todo.toDoListResponseDTOList = (td.toDoListResponseDTOList || []).map(
            (li) => {
              const serverVal =
                li?.completion ??
                li?.completionFlag ??
                (li?.status && String(li.status).toUpperCase() === "DONE");
              return { ...li, completion: Boolean(serverVal) };
            },
          );
          return todo;
        });
        return task;
      });
      setProject(projectData);
      setTasks(normalizedTasks);
    } catch (err) {
      console.error("Error fetching project:", err);
      setProject(null);
      setTasks([]);
    } finally {
      setLoadingProject(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;
    loadProject();
  }, [projectId, loadProject]);

  const fetchProjectMembers = useCallback(
    async (projectIdArg = project?.projectId) => {
      if (!projectIdArg) return;
      try {
        const res = await axiosInstace.get(
          `/api/emp/projects/projectmemberaccess/${projectIdArg}/allmembers`,
        );
        if (res.data?.status === 200 && Array.isArray(res.data.data))
          setMembers(res.data.data);
        else setMembers([]);
      } catch {
        setMembers([]);
      }
    },
    [project?.projectId],
  );

  useEffect(() => {
    if (showInfoModal && project?.projectId)
      fetchProjectMembers(project.projectId);
  }, [showInfoModal, project?.projectId, fetchProjectMembers]);

  const filteredTasks = React.useMemo(() => {
    const list = Array.isArray(tasks) ? tasks : [];
    const q = (filters?.q || "").toLowerCase().trim();
    const out = list.filter((t) => {
      if (filters.status && filters.status !== "all") {
        const s = ((t.status || t.taskStatus || "") + "").toLowerCase();
        if (filters.status === "todo") {
          if (!s.includes("todo") && s !== "not_done" && s !== "pending")
            return false;
        } else if (filters.status === "inprogress") {
          if (
            !s.includes("inprogress") &&
            !s.includes("doing") &&
            s !== "in_progress"
          )
            return false;
        } else if (filters.status === "done") {
          if (!(s.includes("done") || s === "completed" || s === "closed"))
            return false;
        } else {
          if (filters.status !== s) return false;
        }
      }
      if (q) {
        const title = (t.taskTitle || t.title || "").toString().toLowerCase();
        const remark = (t.remark || t.description || t.note || "")
          .toString()
          .toLowerCase();
        if (!`${title} ${remark}`.includes(q)) return false;
      }
      return true;
    });
    const getDateTs = (item) => {
      if (!item) return 0;
      const candidates = [
        item.dueDate,
        item.startDate,
        item.createdAt,
        item.createdDate,
        item.taskCreatedAt,
        item.updatedAt,
      ];
      for (let v of candidates) {
        if (!v) continue;
        if (typeof v === "number" && !Number.isNaN(v)) return v;
        if (v instanceof Date && !Number.isNaN(v.getTime())) return v.getTime();
        const ts = Date.parse(v);
        if (!Number.isNaN(ts)) return ts;
      }
      return 0;
    };
    return [...out].sort((a, b) => {
      const diff = getDateTs(b) - getDateTs(a);
      if (diff === 0)
        return ("" + (b.id ?? b.taskId ?? "")).localeCompare(
          "" + (a.id ?? a.taskId ?? ""),
        );
      return filters.sort === "oldest" ? -diff : diff;
    });
  }, [tasks, filters]);

  useEffect(() => {
    if (showInfoModal && titleRef.current)
      setTimeout(() => titleRef.current.focus(), 50);
  }, [showInfoModal]);

  useEffect(() => {
    if (!creatorEmpId) return;
    prevPermLoadingRef.current = permLoading;
  }, [permLoading, permError, creatorEmpId]);

  if (loadingProject) return <ProjectSkeleton />;
  if (!project)
    return (
      <EmptyState
        icon="🔒"
        title="Project not found"
        description="This project doesn't exist or you don't have access to view it."
      />
    );

  const taskCount = tasks.length;
  const todoCount =
    tasks.reduce(
      (acc, t) =>
        acc +
        (Array.isArray(t.todoRequestDTOList) ? t.todoRequestDTOList.length : 0),
      0,
    ) ?? 0;
  const todoListCount =
    tasks.reduce(
      (acc, t) =>
        acc +
        (t.todoRequestDTOList?.reduce(
          (sub, td) =>
            sub +
            (Array.isArray(td.toDoListResponseDTOList)
              ? td.toDoListResponseDTOList.length
              : 0),
          0,
        ) ?? 0),
      0,
    ) ?? 0;
  // Display-only derivation for the completion progress bar; does not
  // affect any business logic or API calls.
  const completedTodoListCount =
    tasks.reduce(
      (acc, t) =>
        acc +
        (t.todoRequestDTOList?.reduce(
          (sub, td) =>
            sub +
            (Array.isArray(td.toDoListResponseDTOList)
              ? td.toDoListResponseDTOList.filter((li) => li.completion).length
              : 0),
          0,
        ) ?? 0),
      0,
    ) ?? 0;

  // Creator display name for the compact header pill. Falls back through
  // the members list (matched by createdByEmpId) and a couple of possible
  // project-level fields, in case the API shapes differ between endpoints.
  const creatorMember = members.find(
    (m) => String(m.empId) === String(creatorEmpId),
  );
  const creatorName =
    creatorMember?.empName ||
    project?.createdByName ||
    project?.creatorName ||
    "—";

  // The logged-in user's actual role in this project ("Creator", "Approver",
  // "Doer", ...), derived from resolvedUserRole rather than hardcoded.
  const roleLabel = resolvedUserRole
    ? String(resolvedUserRole)
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/^\w/, (c) => c.toUpperCase())
    : "—";
  const currentUserName =
    members.find((m) => String(m.empId) === String(currentEmpId))?.empName ||
    user?.empName ||
    storedUser?.empName ||
    "—";

  // Picks that are actually new: anyone already on the project is ignored.
  const membersToAdd = pendingMembers.filter(
    (pm) =>
      pm.empId &&
      pm.roleId &&
      !members.some((m) => String(m.empId) === String(pm.empId)),
  );
  const alreadyOnProjectCount = pendingMembers.length - membersToAdd.length;

  const openAddMember = () => setShowAddMember(true);

  const closeAddMember = () => {
    setShowAddMember(false);
    setPendingMembers([]);
  };

  const handleAddMember = async () => {
    if (membersToAdd.length === 0) return;
    if (!canAddMembers) {
      setToastMsg("Only the Creator or an Approver can add members");
      setToastType("error");
      setToastOpen(true);
      return;
    }
    setAddingMember(true);
    try {
      const res = await axiosInstace.post(
        MEMBER_API.add(project.projectId),
        {
          projectId: project.projectId,
          memberandRoleRequestList: membersToAdd.map(({ empId, roleId }) => ({
            empId,
            roleId,
          })),
        },
        { headers: { "Content-Type": "application/json" } },
      );
      if (!(res.status >= 200 && res.status < 300))
        throw new Error(res?.data?.message || "Add failed");
      setToastMsg(
        membersToAdd.length === 1
          ? "Member added"
          : `${membersToAdd.length} members added`,
      );
      setToastType("success");
      setToastOpen(true);
      closeAddMember();
      await refreshProjectMembers?.();
      await fetchProjectMembers(project.projectId);
    } catch (err) {
      setToastMsg(
        err?.response?.data?.message || err?.message || "Failed to add member",
      );
      setToastType("error");
      setToastOpen(true);
    } finally {
      setAddingMember(false);
    }
  };

  const handleProjectBasicSave = async () => {
    try {
      const body = {
        projectTitle: project.projectTitle,
        description: project.description,
        startDate: project.startDate,
        endDate: project.endDate,
      };
      const res = await axiosInstace.put(
        `/api/emp/project/${projectId}/basicdetails`,
        body,
      );
      setProject((prev) => ({ ...prev, ...res.data.data }));
      setToastMsg("Project details updated");
      setToastType("success");
      setToastOpen(true);
      setShowInfoModal(false);
    } catch (err) {
      setToastMsg(
        err?.response?.data?.message || "Failed to save project details",
      );
      setToastType("error");
      setToastOpen(true);
    }
  };

  const handleRemoveMember = async (member) => {
    try {
      await axiosInstace.delete(
        `/api/emp/projects/projectmemberaccess/${projectId}/members/${member.empId}`,
      );
      setToastMsg("Member removed");
      setToastType("success");
      setToastOpen(true);
      await refreshProjectFull(projectId);
    } catch (err) {
      const status = err?.response?.status;
      const msg = String(err?.response?.data?.message || "").toLowerCase();
      const isAssignedError =
        msg.includes("assigned") ||
        msg.includes("task") ||
        msg.includes("todo");
      if (status === 400 || status === 409 || isAssignedError) {
        setForceRemoveTarget(member);
        return;
      }
      setToastMsg(err?.response?.data?.message || "Failed to remove member");
      setToastType("error");
      setToastOpen(true);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!taskId) return;
    if (!canPerm("TASK_DELETE")) {
      window.alert("You don't have permission to delete tasks.");
      return;
    }
    const previousTasks = tasks;
    try {
      setTasks((prev) =>
        (prev || []).filter((t) => Number(t.taskId || t.id) !== Number(taskId)),
      );
      setProject((prev) => ({
        ...prev,
        tasks: (prev?.tasks || []).filter(
          (t) => Number(t.taskId || t.id) !== Number(taskId),
        ),
      }));
      const res = await axiosInstace.delete(`/api/emp/tasks/${taskId}`);
      if (!(res.status >= 200 && res.status < 300))
        throw new Error(res.data?.message || "Delete failed");
      try {
        if (!skipAutoRefreshRef.current) refreshProjectMembers();
      } catch {
        /* non-fatal */
      }
      setToastMsg("Stage deleted");
      setToastType("success");
      setToastOpen(true);
    } catch (err) {
      setToastMsg(
        err?.response?.data?.message || err.message || "Failed to delete task",
      );
      setToastType("error");
      setToastOpen(true);
      try {
        const res = await axiosInstace.get(
          `/api/emp/project/${projectId}/full`,
        );
        setProject(res?.data?.data ?? null);
        setTasks(normalizeTasks(res?.data?.data) ?? previousTasks);
      } catch {
        setTasks(previousTasks);
      }
    }
  };

  const updateProjectFullData = async (projectId) => {
    if (!projectId) return null;
    try {
      if (typeof refreshProjectFull === "function")
        await refreshProjectFull(projectId);
    } catch {
      scheduleProjectRefreshDebounced(1200);
    }
    const candidates = [
      `/api/emp/stages/${encodeURIComponent(projectId)}`,
      `/api/emp/projects/${encodeURIComponent(projectId)}`,
      `/api/emp/stages/${encodeURIComponent(projectId)}/details`,
    ];
    for (const path of candidates) {
      try {
        const resp = await axiosInstace.get(path);
        if (resp && (resp.data || resp.status === 200)) {
          const data = resp?.data?.data ?? resp?.data ?? resp;
          try {
            setProject(data);
          } catch {
            /* ignore */
          }
          try {
            const maybeStages =
              data?.data ?? data?.stages ?? data?.stageResponseDTOList ?? data;
            setStages(maybeStages);
          } catch {
            /* ignore */
          }
          return data;
        }
      } catch {
        /* try next */
      }
    }
    if (typeof scheduleProjectRefreshDebounced === "function") {
      scheduleProjectRefreshDebounced(200);
      return null;
    }
    return null;
  };

  const handleAddStage = async (payload) => {
    if (!projectId) {
      setToastMsg("Project id missing");
      setToastType("error");
      setToastOpen(true);
      return null;
    }
    const normalized = {
      stageRequestDTOS: (payload?.stageRequestDTOS || []).map((s) => ({
        ...(s.stageId != null ? { stageId: s.stageId } : {}),
        stageName: s.stageName,
        stageFlag:
          s.stageFlag == null
            ? null
            : String(s.stageFlag).trim().toUpperCase().replace(/\s+/g, "_"),
        stagePositionRequestDTOList: (s.stagePositionRequestDTOList || []).map(
          (p) => {
            const oldStagePositionId =
              p.oldStagePositionId ?? p.stagePositionId ?? p.id ?? null;
            return {
              ...(oldStagePositionId != null ? { oldStagePositionId } : {}),
              position: Number(p.position ?? 0),
              initial: Boolean(p.initial),
              finalPosition: Boolean(p.finalPosition),
            };
          },
        ),
      })),
    };
    try {
      const res = await axiosInstace.post(
        `/api/emp/stages/${encodeURIComponent(projectId)}/add-stages`,
        normalized,
        { headers: { "Content-Type": "application/json" } },
      );
      setToastMsg("Status added");
      setToastType("success");
      setToastOpen(true);
      const newStages =
        res?.data?.data?.stageResponseDTOList ??
        res?.data?.data?.stages ??
        res?.data?.data ??
        null;
      if (newStages && Array.isArray(newStages)) setStages(newStages);
      if (typeof scheduleProjectRefreshDebounced === "function")
        scheduleProjectRefreshDebounced(1200);
      return res?.data?.data ?? res?.data ?? res;
    } catch (err) {
      setToastMsg(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to add/update stages",
      );
      setToastType("error");
      setToastOpen(true);
      return null;
    }
  };

  const handleDeleteTodo = async (todoId) => {
    if (!todoId) return;
    if (!canPerm("TODO_DELETE")) {
      window.alert("You don't have permission to delete ToDos.");
      return;
    }
    const previousTasks = tasks;
    try {
      setTasks((prev) =>
        (prev || []).map((task) => ({
          ...task,
          todoRequestDTOList: (task.todoRequestDTOList || []).filter(
            (td) => Number(td.toDoId || td.id) !== Number(todoId),
          ),
        })),
      );
      setProject((prev) => ({
        ...prev,
        tasks: (prev?.tasks || []).map((task) => ({
          ...task,
          todoRequestDTOList: (task.todoRequestDTOList || []).filter(
            (td) => Number(td.toDoId || td.id) !== Number(todoId),
          ),
        })),
      }));
      const res = await axiosInstace.delete(`/api/emp/todos/${todoId}`);
      if (!(res.status >= 200 && res.status < 300))
        throw new Error(res.data?.message || "Deletion failed");
      try {
        if (!skipAutoRefreshRef.current) refreshProjectMembers();
      } catch {
        /* non-fatal */
      }
      await refreshProjectFull(projectId);
      setToastMsg("Task deleted");
      setToastType("success");
      setToastOpen(true);
    } catch (err) {
      setToastMsg(
        err?.response?.data?.message || err.message || "Failed to delete Task",
      );
      setToastType("error");
      setToastOpen(true);
      try {
        const res = await axiosInstace.get(
          `/api/emp/project/${projectId}/full`,
        );
        const projectData = res?.data?.data ?? null;
        setProject(projectData);
        setTasks(normalizeTasks(projectData) ?? previousTasks);
      } catch {
        setTasks(previousTasks);
      }
    }
  };

  const currentUserIsCreator =
    String(project?.createdByEmpId) === String(currentEmpId);
  const canEditProject =
    !permLoading &&
    typeof canPerm === "function" &&
    canPerm(PERM.PROJECT_UPDATE);

  const quickAddContext = {
    projectId: project?.projectId,
    stagePositionId:
      stages?.[0]?.stagePositionResponseDTOList?.[0]?.stagePositionId ?? null,
    accessId: getCurrentAccessId() ?? project?.accessId ?? null,
    startDate: project?.startDate ?? new Date().toISOString().slice(0, 10),
    endDate: project?.endDate ?? new Date().toISOString().slice(0, 10),
    members,
    currentEmpId,
    currentUserRole: resolvedUserRole,
  };

  const refreshTasks = () => {};
  const isLongProjectTitle = (project.projectTitle || "").trim().length > 28;

  return (
    <div className="project-dashboard">
      {/* ---------- Compact project header ---------- */}
      <div
        className={`project-metadata compact ${
          view === "table" ? "table-view" : ""
        } ${isLongProjectTitle ? "has-long-title" : ""}`}
      >
        <div className="project-meta-left">
          <div
            className={`meta-pill meta-pill--title ${
              isLongProjectTitle ? "title-long" : ""
            }`}
          >
            <span className="label">Title</span>
            <CustomTooltip content={project.projectTitle || "—"}>
              <span className="value truncate">
                {project.projectTitle || "—"}
              </span>
            </CustomTooltip>
          </div>
          {/* Shows the logged-in user's actual role, not the project creator.
              (Class name kept so the existing responsive styles still apply.) */}
          <div className="meta-pill meta-pill--creator">
            <span className="label">{roleLabel}</span>
            <CustomTooltip content={`${currentUserName} (${roleLabel})`}>
              <span className="value truncate">{currentUserName}</span>
            </CustomTooltip>
          </div>
        </div>

        <div className="project-header-right">
          {!permLoading && canPerm("TASK_CREATE") && !isReadOnlyRole && (
            <ButtonWithIcon
              text="Create Stage"
              icon="mdi:plus"
              color="success"
              iconPosition="right"
              onClick={() => setShowCreateTaskModal(true)}
            />
          )}

          <div className="project-header-more" ref={moreMenuRef}>
            <button
              type="button"
              className="project-header-more-toggle"
              aria-haspopup="true"
              aria-expanded={showMoreActions}
              aria-label="More actions"
              onClick={() => setShowMoreActions((v) => !v)}
            >
              <FiMoreVertical aria-hidden="true" />
            </button>

            {showMoreActions && (
              <div className="project-header-more-menu" role="menu">
                <div className="more-menu-pills">
                  <div className="meta-pill">
                    <span className="label">Role</span>
                    <CustomTooltip content={roleLabel}>
                      <span className="value truncate">{roleLabel}</span>
                    </CustomTooltip>
                  </div>
                  <div className="meta-pill status">
                    <span className="label">Status</span>
                    <StatusBadge
                      label={project.projectStatus || "—"}
                      size="sm"
                    />
                  </div>
                </div>

                <hr className="popover-divider" />

                {!permLoading && canPerm("PROJECT_UPDATE") && (
                  <button
                    type="button"
                    className="more-menu-item"
                    role="menuitem"
                    onClick={() => {
                      setShowStageModal(true);
                      setShowMoreActions(false);
                    }}
                  >
                    <img src={StagesIcon} alt="" /> Update Stages
                  </button>
                )}

                {!permLoading && canPerm("PROJECT_UPDATE") && (
                  <button
                    type="button"
                    className="more-menu-item"
                    role="menuitem"
                    onClick={() => {
                      setShowUpdateMembersModal(true);
                      setShowMoreActions(false);
                    }}
                  >
                    <FiUsers aria-hidden="true" /> Update Project Members
                  </button>
                )}

                {!permLoading && canPerm("PROJECT_UPDATE") && (
                  <button
                    type="button"
                    className="more-menu-item"
                    role="menuitem"
                    onClick={() => {
                      setShowDuplicateModal(true);
                      setShowMoreActions(false);
                    }}
                  >
                    <FiCopy aria-hidden="true" /> Duplicate Project
                  </button>
                )}

                <button
                  type="button"
                  className="more-menu-item"
                  role="menuitem"
                  onClick={() => {
                    setShowInfoModal(true);
                    setShowMoreActions(false);
                  }}
                >
                  <FiInfo aria-hidden="true" /> Project Details
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ---------- Info Modal ---------- */}
      {showInfoModal &&
        createPortal(
          <div
            className="pd-overlay"
            role="dialog"
            aria-modal="true"
            onClick={() => setShowInfoModal(false)}
          >
            <div className="pd-modal" onClick={(e) => e.stopPropagation()}>
              <header className="pd-modal-header">
                <CustomTooltip
                  content={`${project.projectTitle || ""} — Details`}
                >
                  <h3 className="truncate">{project.projectTitle} — Details</h3>
                </CustomTooltip>
                <button
                  className="pd-close-btn"
                  onClick={() => setShowInfoModal(false)}
                  aria-label="Close"
                >
                  ✕
                </button>
              </header>

              <section className="pd-modal-body">
                <div className="pd-project-fields">
                  <div className="pd-title-row">
                    <InputField
                      id="project-title"
                      label="Title"
                      type="text"
                      value={project.projectTitle}
                      inputRef={titleRef}
                      readOnly={!canEditProject}
                      autoFocus
                      onChange={(e) =>
                        setProject((prev) => ({
                          ...prev,
                          projectTitle: e.target.value,
                        }))
                      }
                    />
                    <div className="pd-meta-inline">
                      <div className="pd-meta-field">
                        <label>Status</label>
                        <StatusBadge label={project.projectStatus} />
                      </div>
                      <div className="pd-meta-field">
                        <label>Your Role</label>
                        <StatusBadge label={roleLabel} tone="info" />
                      </div>
                      <div className="pd-meta-field">
                        <label>Created By</label>
                        <span className="pd-meta-value truncate">
                          {creatorName}
                        </span>
                      </div>
                    </div>
                  </div>

                  <InputField
                    id="project-description"
                    label="Description"
                    as="textarea"
                    value={project.description}
                    readOnly={!canEditProject}
                    onChange={(e) =>
                      setProject((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                  />

                  <div className="pd-dates-row">
                    <InputField
                      id="project-start-date"
                      label="Start Date"
                      type="date"
                      value={project.startDate}
                      readOnly={!canEditProject}
                      onChange={(e) =>
                        setProject((prev) => ({
                          ...prev,
                          startDate: e.target.value,
                        }))
                      }
                    />
                    <InputField
                      id="project-end-date"
                      label="End Date"
                      type="date"
                      value={project.endDate}
                      readOnly={!canEditProject}
                      onChange={(e) =>
                        setProject((prev) => ({
                          ...prev,
                          endDate: e.target.value,
                        }))
                      }
                    />
                  </div>

                  <div className="pd-action-row">
                    {(isApproverRole || project.role === "APPROVER") && (
                      <DeleteButton
                        size={40}
                        title="Delete Project"
                        tooltip="Delete Project"
                        text="Delete Project"
                        onClick={() => setShowConfirmDelete(true)}
                      />
                    )}
                    {canEditProject && (
                      <button
                        className="btncapsuleprimary"
                        onClick={handleProjectBasicSave}
                      >
                        Save
                      </button>
                    )}
                    <div className="pd-counts">
                      <span>
                        <strong>Tasks:</strong> {taskCount}
                      </span>
                      <span>
                        <strong>To-Dos:</strong> {todoCount}
                      </span>
                      <span>
                        <strong>Checklist:</strong> {todoListCount}
                      </span>
                    </div>
                  </div>

                  {completedTodoListCount != null && todoListCount > 0 && (
                    <ProgressBar
                      label="Checklist completion"
                      value={(completedTodoListCount / todoListCount) * 100}
                    />
                  )}
                </div>

                {/* Members */}
                <div className="pd-members-section">
                  <div className="pd-members-header">
                    <strong className="pd-section-label">
                      Members ({members.length})
                    </strong>
                    {canAddMembers && !showAddMember && (
                      <button
                        type="button"
                        className="btncapsuleprimary pd-add-member-btn"
                        onClick={openAddMember}
                      >
                        Add Member
                      </button>
                    )}
                  </div>

                  {canAddMembers && showAddMember && (
                    <div
                      className="pd-add-member"
                      role="group"
                      aria-label="Add members"
                    >
                      <div className="pd-add-member-picker">
                        <EmployeeTable
                          onAdd={handlePickedMembers}
                          creatorEmpId={creatorEmpId}
                        />
                      </div>

                      {alreadyOnProjectCount > 0 && (
                        <p className="pd-add-member-note">
                          {alreadyOnProjectCount === 1
                            ? "1 selected person is already on this project and will be skipped."
                            : `${alreadyOnProjectCount} selected people are already on this project and will be skipped.`}
                        </p>
                      )}

                      <div className="pd-add-member-actions">
                        <button
                          type="button"
                          className="btncapsuleprimary"
                          disabled={membersToAdd.length === 0 || addingMember}
                          onClick={handleAddMember}
                        >
                          {addingMember
                            ? "Adding…"
                            : membersToAdd.length > 1
                              ? `Add ${membersToAdd.length} Members`
                              : "Add Member"}
                        </button>
                        <button
                          type="button"
                          className="pd-add-member-cancel"
                          disabled={addingMember}
                          onClick={closeAddMember}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="members-list">
                    {members.length === 0 && (
                      <EmptyState
                        compact
                        icon="👥"
                        title="No members yet"
                        description="Add teammates to this project to start collaborating."
                      />
                    )}
                    {members.map((m) => {
                      const empId = m.empId;
                      return (
                        <div key={empId} className="member-item">
                          <div className="member-item-left">
                            <CustomTooltip content={m.empName || "Unknown"}>
                              <div className="avatar">
                                {m.empName?.charAt(0) || "?"}
                              </div>
                            </CustomTooltip>
                            <div className="meta">
                              <CustomTooltip content={m.empName || "—"}>
                                <div className="name truncate">{m.empName}</div>
                              </CustomTooltip>
                              <div className="role truncate">
                                {availableRoles.find(
                                  (r) =>
                                    r.roleId ===
                                    (selectedRoleFor[empId] ?? m.roleId),
                                )?.roleType || m.roleType}
                              </div>
                            </div>
                          </div>

                          <div className="member-item-right">
                            <div className="role-btn-group">
                              {availableRoles.map((role) => (
                                <button
                                  key={role.roleId}
                                  type="button"
                                  className={`role-btn ${(selectedRoleFor[empId] ?? m.roleId) === role.roleId ? "active" : ""}`}
                                  onClick={() =>
                                    setSelectedRoleFor((prev) => ({
                                      ...prev,
                                      [empId]: role.roleId,
                                    }))
                                  }
                                >
                                  {role.roleType}
                                </button>
                              ))}
                            </div>

                            <div className="member-row-actions">
                              {(canPerm("PROJECT_UPDATE") ||
                                canPerm("PROJECT_REMOVE_MEMBER")) && (
                                <button
                                  className="btn primary small"
                                  disabled={
                                    roleUpdatingFor === empId ||
                                    !selectedRoleFor[empId] ||
                                    selectedRoleFor[empId] === m.roleId
                                  }
                                  onClick={async () => {
                                    const roleId = selectedRoleFor[empId];
                                    if (!roleId) return;
                                    setRoleUpdatingFor(empId);
                                    try {
                                      const body = {
                                        projectId: project.projectId,
                                        memberandRoleRequestList: [
                                          { empId, roleId },
                                        ],
                                      };
                                      const res = await axiosInstace.put(
                                        `/api/emp/projects/projectmemberaccess/${project.projectId}/members/update`,
                                        body,
                                        {
                                          headers: {
                                            "Content-Type": "application/json",
                                          },
                                        },
                                      );
                                      if (
                                        !(res.status >= 200 && res.status < 300)
                                      )
                                        throw new Error(
                                          res?.data?.message || "Update failed",
                                        );
                                      setToastMsg("Role updated");
                                      setToastType("success");
                                      setToastOpen(true);
                                      await refreshProjectMembers?.();
                                      scheduleProjectRefreshDebounced(800);
                                      setMembers((prev) =>
                                        prev.map((x) =>
                                          Number(x.empId) === Number(empId)
                                            ? {
                                                ...x,
                                                roleId,
                                                roleType: availableRoles.find(
                                                  (r) => r.roleId === roleId,
                                                )?.roleType,
                                              }
                                            : x,
                                        ),
                                      );
                                      await fetchProjectMembers(
                                        project.projectId,
                                      );
                                    } catch (err) {
                                      setToastMsg(
                                        err?.response?.data?.message ||
                                          err.message ||
                                          "Failed to update role",
                                      );
                                      setToastType("error");
                                      setToastOpen(true);
                                    } finally {
                                      setRoleUpdatingFor(null);
                                    }
                                  }}
                                >
                                  {roleUpdatingFor === empId
                                    ? "Saving…"
                                    : "Save"}
                                </button>
                              )}

                              {canPerm("PROJECT_REMOVE_MEMBER") &&
                                isApproverRole && (
                                  <button
                                    className="btn danger small"
                                    disabled={roleUpdatingFor === empId}
                                    onClick={() => {
                                      if (
                                        currentUserIsCreator &&
                                        (m.roleId === 2 ||
                                          String(m.roleType).toUpperCase() ===
                                            "APPROVER")
                                      ) {
                                        setToastMsg(
                                          "Creators cannot remove Approvers.",
                                        );
                                        setToastType("error");
                                        setToastOpen(true);
                                        return;
                                      }
                                      setMemberToRemove(m); // 👈 set target for confirmation
                                    }}
                                  >
                                    Remove
                                  </button>
                                )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="modal-actions">
                  <button
                    className="btn ghost"
                    onClick={() => setShowInfoModal(false)}
                  >
                    Close
                  </button>
                  <button
                    className="btn capsule primary"
                    onClick={handleProjectBasicSave}
                    disabled={!canEditProject}
                  >
                    Save
                  </button>
                </div>
              </section>
            </div>
          </div>,
          document.body,
        )}

      {/* ---------- Create Task Modal ---------- */}
      {showCreateTaskModal &&
        createPortal(
          <CreateTaskModal
            projectId={projectId}
            creatorEmpId={creatorEmpId}
            stages={stages}
            currentEmpId={currentEmpId}
            onClose={() => setShowCreateTaskModal(false)}
            onCreated={(created) => {
              if (!created) {
                setShowCreateTaskModal(false);
                return;
              }
              if (
                Array.isArray(created.tasks) ||
                Array.isArray(created.taskResponseDTOList)
              ) {
                setProject((prev) => ({ ...(prev || {}), ...(created || {}) }));
                try {
                  setTasks(normalizeTasks(created));
                } catch {
                  setTasks(created.tasks ?? created.taskResponseDTOList ?? []);
                }
              } else {
                setTasks((prev) => [...(prev || []), created]);
                setProject((prev) =>
                  prev
                    ? { ...prev, tasks: [...(prev.tasks || []), created] }
                    : prev,
                );
                if (typeof scheduleProjectRefreshDebounced === "function")
                  scheduleProjectRefreshDebounced(1200);
              }
              setShowCreateTaskModal(false);
            }}
          />,
          document.body,
        )}

      {/* ---------- Main view ---------- */}
      <main className="pd-main">
        <div className="pd-content-card">
          {/* ---------- View toggle + filters ---------- */}
          <div className="pd-toolbar">
            <ViewToggle view={view} handleSwitchView={handleSwitchView} />

            <div className="pd-toolbar-right">
              <FilterBar filters={filters} onChange={(f) => setFilters(f)} />
            </div>
          </div>

          {(() => {
            const commonProps = {
              tasks: filteredTasks,
              stages,
              currentUserId: user?.empId,
              projectEndDate: project?.endDate,
              handleUpdateTask,
              handleUpdateTodo,
              onChangeTaskStage: handleChangeTaskStage,
              onChangeTodoStage: handleChangeTodoStage,
              onUpdateTodo: handleUpdateTodo,
              onUpdateTodoList: handleUpdateTodoList,
              onToggleChecklist: handleToggleTodoListCompletion,
              onUpdateChecklistRemark: handleUpdateChecklistRemark,
              stageChangeLoading,
              onSelectItem: setSelectedItem,
              openTaskModal,
              setOpenTaskModal,
              openTodoModal,
              setOpenTodoModal,
              currentUserRole: resolvedUserRole,
              currentEmpId,
              currentAccessId: project?.accessId,
              projectMembers: members,
              onCreateTodo: handleCreateTodo,
              onCreateTodoList: handleCreateTodoList,
              onDeleteTask: handleDeleteTask,
              onDeleteTodo: handleDeleteTodo,
              onDeleteTodoList: handleDeleteTodoList,
            };

            switch (view) {
              case "table":
                return (
                  <TableView
                    {...commonProps}
                    project={project}
                    setTasks={setTasks}
                    filters={filters}
                    refreshProject={refreshProjectFull}
                    canDeleteTasks={canDelete}
                    canUpdateTask={!permLoading && canPerm("TASK_UPDATE")}
                    canUpdateTodo={!permLoading && canPerm("TODO_UPDATE")}
                    canUpdateTodoList={
                      !permLoading && canPerm("TODOLIST_UPDATE")
                    }
                    canChangeTodoStageFor={canChangeTodoStageFor}
                    onOpenTaskModal={setOpenTaskModal}
                    onOpenTodoRemarkModal={handleOpenTodoRemarkModal}
                  />
                );

              case "kanban":
                return (
                  <KanbanView {...commonProps} parentSubmit={handleAddStage} />
                );

              case "gantt":
                return <GanttView tasks={tasks} />;

              case "list":
                return <ListView {...commonProps} />;

              default:
                return null;
            }
          })()}
        </div>

        {selectedItem && (
          <DetailsModal
            item={selectedItem}
            currentUserRole={resolvedUserRole}
            currentEmpId={currentEmpId}
            currentUserId={user?.empId}
            onDeleteTask={handleDeleteTask}
            parentTaskOwnerId={
              selectedItem?.parentTaskOwnerId ||
              selectedItem?.parentTask?.ownerId ||
              selectedItem?.taskOwnerId ||
              selectedItem?.task?.ownerId ||
              null
            }
            projectEndDate={project?.endDate}
            projectMembers={members}
            onClose={() => setSelectedItem(null)}
            onSave={(item, formData) => {
              if (item.taskId)
                return handleUpdateTask(item.taskId, {
                  taskTitle: formData.title,
                  description: formData.description,
                  remark: formData.remark,
                  priority: formData.priority,
                  startDate: formData.startDate,
                  endDate: formData.endDate,
                  schedulingMode: formData.schedulingMode,
                  durationDays: formData.durationDays,
                  accessId:
                    formData.accessId ?? formData.access?.accessId ?? null,
                  assignedEmpId:
                    formData.assignedEmpId ??
                    formData.assignedEmp?.empId ??
                    null,
                  taskOwnerId:
                    formData.taskOwnerId ?? formData.ownerEmpId ?? null,
                });

              if (item.toDoId)
                return handleUpdateTodo(item.toDoId, {
                  toDoTitle: formData.title ?? formData.toDoTitle ?? "",
                  remark: formData.remark,
                  accessId: formData.accessId ?? null,
                  assignedEmpId: formData.assignedEmpId ?? null,
                });
            }}
          />
        )}
      </main>

      {/* ---------- Simple task detail modal ---------- */}
      {openTaskModal && (
        <div className="modalfortask-overlay">
          <div className="modalfortask-content">
            <h3>{openTaskModal.taskTitle || "Task"}</h3>
            <p>
              <strong>Description:</strong>{" "}
              {openTaskModal.description || "No description"}
            </p>
            <button
              className="btn-cancel"
              onClick={() => setOpenTaskModal(null)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* ---------- Edit Stages Modal ---------- */}
      {showStageModal &&
        createPortal(
          <EditStagesModal
            projectId={projectId}
            onClose={() => setShowStageModal(false)}
            onSuccess={async () => {
              try {
                if (typeof updateProjectFullData === "function")
                  await updateProjectFullData(projectId);
                else if (typeof refreshProjectFull === "function")
                  await refreshProjectFull(projectId);
                setToastMsg("Stages updated");
                setToastType("success");
                setToastOpen(true);
              } catch {
                setToastMsg(
                  "Stages updated (failed to refresh view). Try manual refresh.",
                );
                setToastType("warning");
                setToastOpen(true);
              } finally {
                setShowStageModal(false);
              }
            }}
            parentSubmit={handleAddStage}
          />,
          document.body,
        )}

      {/* ---------- Remark modal ---------- */}
      {remarkModal.open && (
        <div
          className="modalfortask-overlay"
          onClick={handleCloseTodoRemarkModal}
        >
          <div
            className="modalfortask-content"
            onClick={(e) => e.stopPropagation()}
          >
            <CustomTooltip content={remarkModal.title || "Todo Remark"}>
              <h3 className="truncate">{remarkModal.title || "Todo Remark"}</h3>
            </CustomTooltip>
            <p>
              <strong>Remark:</strong>{" "}
              {remarkModal.remark || "No remark provided"}
            </p>
            <button className="btn-cancel" onClick={handleCloseTodoRemarkModal}>
              Close
            </button>
          </div>
        </div>
      )}

      {/* ---------- Todo detail modal ---------- */}
      {openTodoModal && (
        <div
          className="modalfortask-overlay"
          onClick={() => setOpenTodoModal(null)}
        >
          <div
            className="modalfortask-content"
            onClick={(e) => e.stopPropagation()}
          >
            <CustomTooltip content={openTodoModal.toDoTitle || "Todo"}>
              <h3 className="truncate">{openTodoModal.toDoTitle || "Todo"}</h3>
            </CustomTooltip>
            <div className="todo-modal-fields">
              <p>
                <strong>Remark:</strong> {openTodoModal.remark || "—"}
              </p>
              <p className="truncate-row">
                <strong>Task:</strong>{" "}
                <CustomTooltip content={openTodoModal.taskTitle || "—"}>
                  <span className="truncate">
                    {openTodoModal.taskTitle || "—"}
                  </span>
                </CustomTooltip>
              </p>
              <p>
                <strong>Task Owner:</strong> {openTodoModal.taskOwner || "—"}
              </p>
              <p>
                <strong>Priority:</strong>{" "}
                {openTodoModal.taskPriority ? (
                  <StatusBadge label={openTodoModal.taskPriority} size="sm" />
                ) : (
                  "—"
                )}
              </p>
              <p>
                <strong>Start date:</strong> {fmt(openTodoModal?.startDate)}
              </p>
              <p>
                <strong>End date:</strong> {fmt(openTodoModal?.endDate)}
              </p>
            </div>
            <button
              className="btn-cancel"
              onClick={() => setOpenTodoModal(null)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* ---------- Update Members ---------- */}
      {showUpdateMembersModal &&
        createPortal(
          <div
            className="pd-overlay um223_portalOverlay"
            role="dialog"
            aria-modal="true"
            onClick={() => setShowUpdateMembersModal(false)}
          >
            <div
              className="pd-modal-center um223_portalCenter"
              onClick={(e) => e.stopPropagation()}
            >
              <UpdateProjectMembers
                projectId={project.projectId}
                currentEmpId={user?.empId}
                creatorEmpId={creatorEmpId}
                canPerm={canPerm}
                refreshProjectMembers={refreshProjectMembers}
                onCancel={() => setShowUpdateMembersModal(false)}
                onConfirm={() => {
                  setShowUpdateMembersModal(false);
                  if (typeof refreshProjectMembers === "function")
                    refreshProjectMembers();
                }}
              />
            </div>
          </div>,
          document.body,
        )}

      {/* ---------- Duplicate Modal ---------- */}
      {showDuplicateModal &&
        createPortal(
          <DuplicateProjectModal
            project={project}
            tasks={tasks}
            members={members}
            currentEmpId={currentEmpId}
            axiosInstance={axiosInstace}
            availableRoles={availableRoles}
            onClose={() => setShowDuplicateModal(false)}
            onSuccess={(newProject) => {
              setShowDuplicateModal(false);
              setToastMsg(
                `Project duplicated${newProject?.projectTitle ? `: "${newProject.projectTitle}"` : " successfully"}`,
              );
              setToastType("success");
              setToastOpen(true);
              window.dispatchEvent(
                new CustomEvent("project-updated", {
                  detail: { action: "duplicated", newProject },
                }),
              );
            }}
          />,
          document.body,
        )}

      {toastOpen && (
        <Toast message={toastMsg} type={toastType} onClose={closeToast} />
      )}

      {/* Informational warning — tasks still exist */}
      {showDeleteWarning && (
        <DeleteWarningModal
          message="Please delete all tasks in this project before deleting the project."
          onClose={() => setShowDeleteWarning(false)}
          type="info" // 👈 "info" hides the confirm button
        />
      )}

      {/* Confirmation — safe to delete */}
      {showConfirmDelete && (
        <DeleteWarningModal
          message="Are you sure you want to delete this project? This cannot be undone."
          type="warning"
          confirmText="Yes, Delete"
          onConfirm={() => {
            setShowConfirmDelete(false);
            handleDeleteProject();
          }}
          onClose={() => setShowConfirmDelete(false)}
        />
      )}

      {forceDeleteState.open && (
        <DeleteWarningModal
          message={`${forceDeleteState.message} Do you want to force delete this project anyway? This will permanently remove all its tasks and data.`}
          type="warning"
          confirmText="Yes, Force Delete"
          onConfirm={() => {
            setForceDeleteState({ open: false, message: "" });
            handleForceDeleteProject();
          }}
          onClose={() => setForceDeleteState({ open: false, message: "" })}
        />
      )}
      {showQuickAddModal && (
        <QuickAddModal
          open={showQuickAddModal}
          onClose={() => setShowQuickAddModal(false)}
          context={quickAddContext}
          onCreated={() => refreshTasks()}
        />
      )}
      {forceRemoveTarget && (
        <ForceRemoveMemberModal
          projectId={projectId}
          member={forceRemoveTarget}
          onClose={() => setForceRemoveTarget(null)}
          onSuccess={async (msg) => {
            setToastMsg(msg || "Member forcefully removed");
            setToastType("success");
            setToastOpen(true);
            setForceRemoveTarget(null);
            await refreshProjectFull(projectId);
          }}
          onError={(msg) => {
            setToastMsg(msg);
            setToastType("error");
            setToastOpen(true);
          }}
        />
      )}

      {memberToRemove && (
        <DeleteWarningModal
          message={`Are you sure you want to remove "${memberToRemove.empName}" from this project?`}
          type="warning"
          confirmText="Yes, Remove"
          onConfirm={() => {
            handleRemoveMember(memberToRemove);
            setMemberToRemove(null);
          }}
          onClose={() => setMemberToRemove(null)}
        />
      )}
    </div>
  );
}
